import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetUser, track } from '../utils/analytics';
import { DELIVERY_ENABLED } from '../utils/features';
import ConsultantModal from './ConsultantModal';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [consultantOpen, setConsultantOpen] = useState(false);

  const handleLogout = () => {
    track('user_logged_out');
    resetUser();
    localStorage.removeItem('guruone_token');
    localStorage.removeItem('guruone_user');
    // Clear global per-session flags so a fresh sign-in (incl. a new
    // account on the same browser) doesn't inherit the previous user's
    // session state. Per-user-scoped data (anything keyed by user_X)
    // is left alone — only the global flags get wiped here.
    try {
      localStorage.removeItem('guruone_delivery_journey');
      // Survey snooze + shown count are intentionally retained — the
      // platform's first-impressions survey is a one-per-account thing
      // handled by the survey backend, not local-only state.
    } catch { /* non-fatal */ }
    navigate('/login');
  };

  const handleBookCall = () => {
    track('book_call_button_clicked', { source: 'header', pathname: location.pathname });
    setConsultantOpen(true);
  };

  const user = (() => {
    try { return JSON.parse(localStorage.getItem('guruone_user')); } catch { return null; }
  })();

  const isActive = (p) => location.pathname === p;

  const navBtn = (path, label, soon) => (
    <button key={path} onClick={() => !soon && navigate(path)}
      style={{
        background: isActive(path) ? 'rgba(255,255,255,0.12)' : 'transparent',
        border: 'none',
        color: soon ? 'rgba(255,255,255,0.35)' : isActive(path) ? 'white' : 'rgba(255,255,255,0.8)',
        fontSize: '0.78rem', padding: '0.3rem 0.85rem', borderRadius: 20,
        cursor: soon ? 'default' : 'pointer', width: 'auto', whiteSpace: 'nowrap',
        fontFamily: 'inherit', fontWeight: isActive(path) ? 600 : 400, transition: 'all 0.15s',
      }}>
      {label}{soon && <span style={{ fontSize: '0.52rem', marginLeft: 4, opacity: 0.7 }}>soon</span>}
    </button>
  );

  const subtleBtn = (path, label) => (
    <button
      onClick={() => navigate(path)}
      style={{
        background: 'none', border: 'none',
        color: isActive(path) ? 'white' : 'rgba(255,255,255,0.6)',
        fontSize: '0.7rem', padding: '0.25rem 0.6rem', borderRadius: 20,
        cursor: 'pointer', width: 'auto', fontFamily: 'inherit', transition: 'color 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.color = 'white'; }}
      onMouseLeave={e => { e.currentTarget.style.color = isActive(path) ? 'white' : 'rgba(255,255,255,0.6)'; }}
    >
      {label}
    </button>
  );

  return (
    <>
      <header style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '0 1.5rem', height: 54,
        display: 'flex', alignItems: 'center', gap: '0.25rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <img
          src="/Logo__long_.png"
          alt="GuruOne"
          onClick={() => navigate('/dashboard')}
          style={{ height: 34, cursor: 'pointer', marginRight: '1rem', flexShrink: 0 }}
        />

        {/* Primary nav — order follows the journey: Direction → Design → Delivery,
            with Decision (the cross-cutting NED) at the end. */}
        <nav style={{ display: 'flex', gap: '0.1rem', flex: 1 }}>
          {navBtn('/dashboard', 'Home')}
          {navBtn('/strategy', 'Direction Guru')}
          {navBtn('/operating-model', 'Design Guru')}
          {navBtn('/delivery', 'Delivery Guru', !DELIVERY_ENABLED)}
          {navBtn('/decision', 'Decision Guru')}
        </nav>

        {/* Right side */}
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
          {subtleBtn('/how-it-works', 'How it works')}
          {subtleBtn('/feedback', 'Give feedback')}
          {subtleBtn('/settings', 'Settings')}

          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)' }} />

          {/* Book a 15min call — always visible on authenticated pages.
              Opens the shared Consultant modal. Styled to match the
              other right-side link buttons (subtleBtn) so it sits
              comfortably alongside How it works / Give feedback /
              Settings rather than competing for attention. */}
          <button
            onClick={handleBookCall}
            style={{
              background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.7rem', padding: '0.25rem 0.6rem', borderRadius: 20,
              cursor: 'pointer', width: 'auto', whiteSpace: 'nowrap',
              fontFamily: 'inherit', transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
          >
            Book a 15min call
          </button>

          {user?.businessName && (
            <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.businessName}
            </span>
          )}

          <button onClick={handleLogout}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', cursor: 'pointer', width: 'auto', padding: '0.25rem 0.4rem', fontFamily: 'inherit' }}>
            Log out
          </button>
        </div>
      </header>

      <ConsultantModal open={consultantOpen} onClose={() => setConsultantOpen(false)} />
    </>
  );
}
